/**
 * Single source of truth for funds-transfer pricing off the Treasury curve.
 * Both Manage Targets and Segments (projected basis) call ftpForWal — there is
 * deliberately no second copy of this curve anywhere in the app.
 */

/** Mock Treasury curve: tenor in years → par yield in percent. */
export const TREASURY_CURVE: Array<{ years: number; rate: number }> = [
  { years: 1, rate: 3.62 },
  { years: 2, rate: 3.48 },
  { years: 3, rate: 3.45 },
  { years: 5, rate: 3.58 },
  { years: 7, rate: 3.76 },
  { years: 10, rate: 3.94 },
];

/** Fixed funding spread over the curve, in percent. */
export const FTP_SPREAD = 0.28;

/** Linear interpolation between curve points; flat outside the ends. */
export function treasuryRateFor(walYears: number): number {
  const points = TREASURY_CURVE;
  const first = points[0]!;
  const last = points[points.length - 1]!;
  if (walYears <= first.years) return first.rate;
  if (walYears >= last.years) return last.rate;

  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i]!;
    const b = points[i + 1]!;
    if (walYears >= a.years && walYears <= b.years) {
      const t = (walYears - a.years) / (b.years - a.years);
      return a.rate + t * (b.rate - a.rate);
    }
  }
  return last.rate;
}

/** Today's forward-looking FTP for a given weighted average life. */
export function ftpForWal(walYears: number): number {
  return Number((treasuryRateFor(walYears) + FTP_SPREAD).toFixed(2));
}

/**
 * Gross margin grosses the target net margin up for the internal commission
 * paid out of it: grossMargin = targetNetMargin / (1 - internalCommissionRate).
 * Rates are held as percentages (18 means 18%).
 */
export function grossMarginFor(targetNetMargin: number, internalCommissionRatePct: number): number {
  const rate = Math.min(Math.max(internalCommissionRatePct, 0), 95) / 100;
  return Number((targetNetMargin / (1 - rate)).toFixed(2));
}
